import { products, printAreas, pricingRules } from '../data/products.js';
import { DEFAULT_DISCOUNT_RATE } from './discountHelpers.js';
import { DELIVERY_PER_50_UNITS, EMBO_DEV_FEE, EMBO_UNIT_FEE, extractVatFromGross } from './pricingConstants.js';
import { validatePrintAreaSelections } from './printAreaRules.js';

function normalizeSizeMatrices(sizeMatrices) {
  if (!sizeMatrices || typeof sizeMatrices !== 'object' || Array.isArray(sizeMatrices)) return {};
  const normalized = {};
  for (const color of Object.keys(sizeMatrices)) {
    const matrix = sizeMatrices[color];
    if (!matrix || typeof matrix !== 'object' || Array.isArray(matrix)) continue;
    normalized[color] = {};
    for (const size of Object.keys(matrix)) {
      const qty = Number(matrix[size]) || 0;
      normalized[color][size] = qty;
    }
  }
  return normalized;
}

function calculateTotalQty(sizeMatrices) {
  return Object.values(sizeMatrices).reduce((sum, matrix) => {
    if (!matrix || typeof matrix !== 'object') return sum;
    return sum + Object.values(matrix).reduce((inner, qty) => inner + (Number(qty) || 0), 0);
  }, 0);
}

function resolveTierUnitPrice(sku, totalQty) {
  const rules = pricingRules[sku];
  if (!rules || !Array.isArray(rules.tiers) || rules.tiers.length === 0) {
    return null;
  }

  const tier = rules.tiers.find((t) => totalQty >= Number(t.min) && totalQty <= Number(t.max));
  if (tier) return Number(tier.price) || 0;

  const lastTier = rules.tiers[rules.tiers.length - 1];
  return Number(lastTier?.price) || 0;
}

/**
 * Shared gross/VAT-inclusive pricing calculator used by the configurator and WhatsApp bot.
 * It intentionally preserves sizeMatrices and selectedPrintAreas dimensions.
 */
export function calculatePricing({
  sku,
  sizeMatrices,
  selectedPrintAreas,
  withDelivery = false,
  discountClaimed = false,
  discountRate = DEFAULT_DISCOUNT_RATE,
}) {
  const product = products.find((p) => p.sku === sku);
  if (!product || !Array.isArray(pricingRules[sku]?.tiers) || pricingRules[sku].tiers.length === 0) {
    return {
      ok: false,
      errors: [!product ? 'unknown_sku' : 'missing_approved_tier'],
      totalQty: 0,
      discountRate: 0,
      discountClaimed: false,
      discountAmount: 0,
      subtotalBeforeDiscount: 0,
      subtotalAfterDiscount: 0,
      totalBeforeDiscount: 0,
      totalIls: 0,
      breakdown: {},
    };
  }

  const normalizedMatrices = normalizeSizeMatrices(sizeMatrices);
  const validation = validatePrintAreaSelections({ sku, selectedPrintAreas });
  const normalizedAreas = validation.normalizedAreas;
  const totalQty = calculateTotalQty(normalizedMatrices);

  if (!validation.valid) {
    return {
      ok: false,
      errors: validation.errors,
      totalQty,
      discountRate: 0,
      discountClaimed: Boolean(discountClaimed),
      discountAmount: 0,
      subtotalBeforeDiscount: 0,
      subtotalAfterDiscount: 0,
      totalBeforeDiscount: 0,
      totalIls: 0,
      breakdown: {},
      normalized: {
        sizeMatrices: normalizedMatrices,
        selectedPrintAreas: normalizedAreas,
      },
    };
  }

  if (totalQty === 0) {
    return {
      ok: true,
      errors: [],
      totalQty: 0,
      discountRate: 0,
      discountClaimed: Boolean(discountClaimed),
      discountAmount: 0,
      subtotalBeforeDiscount: 0,
      subtotalAfterDiscount: 0,
      totalBeforeDiscount: 0,
      totalIls: 0,
      breakdown: {},
      normalized: {
        sizeMatrices: normalizedMatrices,
        selectedPrintAreas: normalizedAreas,
      },
    };
  }

  const unitPrice = resolveTierUnitPrice(product.sku, totalQty);
  const baseTotal = unitPrice * totalQty;

  let placementFeesTotal = 0;
  let emboUnitsCount = 0;

  for (const sel of normalizedAreas) {
    const area = printAreas[sel.areaKey];
    if (!area) continue;
    if (sel.method === 'embo') {
      emboUnitsCount += 1;
    } else {
      placementFeesTotal += (Number(area.fee) || 0) * totalQty;
    }
  }

  const emboFeeTotal = emboUnitsCount > 0 ? EMBO_DEV_FEE + (EMBO_UNIT_FEE * totalQty * emboUnitsCount) : 0;
  const deliveryCost = withDelivery ? Math.ceil(totalQty / 50) * DELIVERY_PER_50_UNITS : 0;

  const merchandiseTotal = baseTotal + placementFeesTotal + emboFeeTotal;
  const appliedDiscountRate = discountClaimed ? Number(discountRate || 0) : 0;
  const discountAmount = Number(Math.min(merchandiseTotal * appliedDiscountRate, merchandiseTotal).toFixed(2));
  const subtotalAfterDiscount = Math.max(merchandiseTotal - discountAmount, 0);
  const totalBeforeDiscount = merchandiseTotal + deliveryCost;
  const totalAfterDiscount = subtotalAfterDiscount + deliveryCost;

  const placementFeesPerUnit = normalizedAreas.reduce((sum, sel) => {
    const area = printAreas[sel.areaKey];
    if (!area) return sum;
    if (sel.method === 'embo') return sum;
    return sum + (Number(area.fee) || 0);
  }, 0);

  return {
    ok: true,
    priceIncludesVat: true,
    errors: [],
    totalQty,
    discountRate: appliedDiscountRate,
    discountClaimed: Boolean(discountClaimed),
    discountAmount,
    subtotalBeforeDiscount: merchandiseTotal,
    subtotalAfterDiscount,
    totalBeforeDiscount,
    totalIls: totalAfterDiscount,
    vatBase: extractVatFromGross(totalAfterDiscount).net,
    vatAmount: extractVatFromGross(totalAfterDiscount).vat,
    breakdown: {
      unitBase: unitPrice,
      baseTotal,
      placementFeesPerUnit,
      placementFeesTotal,
      emboUnitsCount,
      emboFeeTotal,
      deliveryCost,
      merchandiseTotal,
      discountRate: appliedDiscountRate,
      discountAmount,
      subtotalAfterDiscount,
      totalBeforeDiscount,
      totalAfterDiscount,
    },
    normalized: {
      sizeMatrices: normalizedMatrices,
      selectedPrintAreas: normalizedAreas,
    },
  };
}

export default calculatePricing;
